'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import { connect } from 'react-redux';
import I18n from 'I18n';
import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { fetchOverview, fetchMyPermissions } from 'AdsUI/actions/AdAccountsActions';
import ConnectedUsersActions from 'AdsUI/actions/ConnectedUsersActions';
import { trackEvent, usageTracker } from 'AdsUI/lib/usageTracker';
import { getConnectedNetworks } from 'AdsUI/selectors/AccountsSelectors';
import UILink from 'UIComponents/link/UILink';
import AccountConnectContainer from 'ads-account-connection/networks/AccountConnectContainer';
import { Metrics } from 'AdsUI/Metrics';
const ErrorMessageMap = {
  'AuthenticationError.CUSTOMER_NOT_FOUND': 'adWordsCustomerNotFound'
};

const CreateAdAccountSetup = ({
  auth,
  onClose,
  connectedNetworks,
  fetchConnectedUsers,
  networkSelected,
  isDeveloperPortal
}) => {
  const onAccountFailure = useCallback((network, err) => {
    trackEvent('accountConnectFailure', {
      network
    });
    let title = 'ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.title';
    let messageKey = 'ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.message';
    let useJsx = false;

    if (err && err.responseJSON) {
      const errorType = err.responseJSON.errorType;
      const errorSubtype = err.responseJSON.errorSubtype;

      if (I18n.lookup(`ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.errors.${errorType}.${errorSubtype}_jsx`)) {
        messageKey = `ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.errors.${errorType}.${errorSubtype}_jsx`;
        useJsx = true;
      } else if (I18n.lookup(`ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.${errorType}`)) {
        if (I18n.lookup(`ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.${errorType}.message`)) {
          title = `ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.${errorType}.title`;
          messageKey = `ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.${errorType}.message`;
        } else {
          messageKey = `ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.${errorType}`;
        }
      } else if (ErrorMessageMap[err.responseJSON.message]) {
        messageKey = `ADS_ACCOUNT_CONNECTION.connectAccountErrorBanner.${ErrorMessageMap[err.responseJSON.message]}`;
      }
    }

    postNotification({
      id: 'ad-account-connection-failure',
      type: 'danger',
      message: useJsx ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: messageKey,
        elements: {
          Link: UILink
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: messageKey,
        options: {
          network: I18n.text(`ADS_ACCOUNT_CONNECTION.networks.${network.toLowerCase()}`)
        }
      }),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: title,
        options: {
          network: I18n.text(`ADS_ACCOUNT_CONNECTION.networks.${network.toLowerCase()}`)
        }
      })
    });
  }, []);
  const onNetworkFailure = useCallback(({
    network
  }) => {
    trackEvent('networkConnectFailure', {
      network
    });
  }, []);
  const onAccountConnected = useCallback(({
    available,
    network,
    selected
  }) => {
    if (!selected || selected.size === 0) {
      return;
    }

    Metrics.counter('connect-account-account-setup', {
      network
    }).increment();
    fetchOverview();
    fetchMyPermissions(); // ********** PUBLIC EVENT **********
    // Public Events help teams across HubSpot automate work and customize experiences based on user actions.
    // Speak with #product-insight and your PM before any shipping any changes to this event incl. event name, properties, values, and when it occurs.
    // Read more about Public Events on the wiki: https://wiki.hubspotcentral.net/display/PM/Public+Events+-+Amplitude+events+ready+for+HubSpot+team+use+and+automation

    usageTracker.track('accountConnectSuccess', {
      available: available ? available.size : 0,
      selected: selected ? selected.size : 0,
      tracked: selected ? selected.filter(a => a).size : 0,
      selectedNew: selected ? selected.size : 0,
      selectedMultiUser: 0,
      network
    });
  }, []);
  const onNetworkConnected = useCallback(network => {
    usageTracker.track('networkConnectSuccess', {
      connected: connectedNetworks.size + 1,
      network
    });
    Metrics.counter('connect-network-account-setup', {
      network
    }).increment();
    fetchConnectedUsers().catch(() => {// failed request is handled by action wrapper to display error alert
      // catch is needed to prevent unhandled promise rejection
    });
  }, [connectedNetworks, fetchConnectedUsers]);

  const getConnectContainerProps = () => {
    return {
      network: networkSelected,
      onFailure: error => onAccountFailure(networkSelected, error),
      auth,
      maxSelectableAccounts: 1,
      onClickCreateAdAccount: () => trackEvent('adAccountConnectAction', {
        action: 'Clicked create ad account',
        network: networkSelected
      }),
      allowDeselectInModals: true,
      onAccountConnected,
      onNetworkConnected,
      onComplete: onClose,
      onOAuthFailure: onNetworkFailure,
      trackEvent,
      forceReauth: true,
      adwordsSelectedFromOnboarding: true,
      isDeveloperPortal
    };
  };

  return /*#__PURE__*/_jsx(AccountConnectContainer, Object.assign({}, getConnectContainerProps()));
};

export default connect(state => ({
  auth: state.auth,
  connectedNetworks: getConnectedNetworks(state)
}), {
  fetchOverview,
  fetchMyPermissions,
  fetchConnectedUsers: ConnectedUsersActions.fetch,
  postNotification
})(CreateAdAccountSetup);